import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Trash2, Upload, Download, FileText, File } from 'lucide-react';
import { toast } from 'sonner';
import { adminDocumentsAPI } from '../../services/adminApi';

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: 'general'
  });

  const fetchDocuments = async () => {
    try {
      const data = await adminDocumentsAPI.getAll();
      setDocuments(data);
    } catch (err) {
      toast.error('Dokümanlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    
    if (!file) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }

    if (!uploadForm.name.trim()) {
      toast.error('Lütfen bir isim girin');
      return;
    }

    setUploading(true);

    try {
      await adminDocumentsAPI.upload(
        file, 
        uploadForm.name, 
        uploadForm.description, 
        uploadForm.category
      );
      toast.success('Doküman yüklendi');
      setUploadForm({ name: '', description: '', category: 'general' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || 'Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu doküman silinecek. Emin misiniz?')) return;
    
    try {
      await adminDocumentsAPI.delete(id);
      toast.success('Doküman silindi');
      fetchDocuments();
    } catch (err) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const categoryLabels = {
    technical_report: 'Teknik Rapor',
    design_document: 'Tasarım Dokümanı',
    presentation: 'Sunum',
    general: 'Genel'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-documents">
      <h1 className="text-2xl font-bold mb-6" style={{ color: '#F8FAFC' }}>Dokümanlar</h1>

      {/* Upload Form */}
      <form onSubmit={handleUpload} className="glass-card p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4" style={{ color: '#F8FAFC' }}>
          Yeni Doküman Yükle
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Dosya *</label>
            <input
              type="file"
              ref={fileInputRef}
              className="w-full px-3 py-2 text-sm"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
            />
          </div>
          <div>
            <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>İsim *</label>
            <input
              type="text"
              value={uploadForm.name}
              onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
              className="w-full px-3 py-2"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
              placeholder="Technical Design Report"
            />
          </div>
          <div>
            <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Kategori</label>
            <select
              value={uploadForm.category}
              onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
              className="w-full px-3 py-2"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
            >
              <option value="technical_report">Teknik Rapor</option>
              <option value="design_document">Tasarım Dokümanı</option>
              <option value="presentation">Sunum</option>
              <option value="general">Genel</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium"
              style={{
                background: '#EC4899',
                color: '#F8FAFC'
              }}
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Upload size={18} />
                  Yükle
                </>
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>Açıklama</label>
          <input
            type="text"
            value={uploadForm.description}
            onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
            className="w-full px-3 py-2"
            style={{
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(0, 240, 255, 0.3)',
              color: '#F8FAFC'
            }}
            placeholder="Doküman açıklaması (opsiyonel)"
          />
        </div>
      </form>

      {/* Documents List */}
      <div className="glass-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr style={{ background: 'rgba(15, 23, 42, 0.8)', borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
              <th className="text-left p-4" style={{ color: '#F8FAFC' }}>Doküman</th>
              <th className="text-left p-4" style={{ color: '#F8FAFC' }}>Kategori</th>
              <th className="text-left p-4" style={{ color: '#F8FAFC' }}>Boyut</th>
              <th className="text-left p-4" style={{ color: '#F8FAFC' }}>Tarih</th>
              <th className="text-right p-4" style={{ color: '#F8FAFC' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id} style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.1)' }}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <FileText size={20} style={{ color: '#EC4899' }} />
                    <div>
                      <p style={{ color: '#F8FAFC' }}>{doc.name}</p>
                      <p className="text-xs" style={{ color: '#64748B' }}>{doc.filename}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-xs px-2 py-1 rounded" style={{ 
                    background: 'rgba(236, 72, 153, 0.2)', 
                    color: '#EC4899' 
                  }}>
                    {categoryLabels[doc.category] || doc.category}
                  </span>
                </td>
                <td className="p-4" style={{ color: '#94A3B8' }}>
                  {formatFileSize(doc.file_size)}
                </td>
                <td className="p-4" style={{ color: '#94A3B8' }}>
                  {new Date(doc.uploaded_at).toLocaleDateString('tr-TR')}
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={adminDocumentsAPI.getDownloadUrl(doc.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: '#4ADE80', background: 'rgba(74, 222, 128, 0.1)' }}
                    >
                      <Download size={16} />
                    </a>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && (
          <div className="p-8 text-center" style={{ color: '#94A3B8' }}>
            Henüz doküman yok
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDocuments;
